import React, { useEffect, useState } from 'react';
import { Globe2, Link2, Users, Eye, Plus, Settings2, ExternalLink } from 'lucide-react';
import api from '../api/client';
import FormDialog from '../components/FormDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailSheet from '../components/DetailSheet';
import RowActions from '../components/RowActions';
import { toast } from '../hooks/use-toast';

const FIELDS = [
  { name: 'company', label: 'Nom de l\u2019entreprise', required: true, full: true },
  { name: 'users', label: 'Utilisateurs', type: 'number' },
  { name: 'sessions', label: 'Sessions partagées', type: 'number' },
];

export default function Extranet() {
  const [extranetClients, setClients] = useState([]);
  const [create, setCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get('/extranet').then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const handleCreate = async (values) => {
    const slug = (values.company || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const payload = { ...values, users: Number(values.users) || 0, sessions: Number(values.sessions) || 0, lastLogin: 'jamais', portal: `${slug}.portal.academie.fr` };
    const { data } = await api.post('/extranet', payload);
    setClients((p) => [...p, data]);
    toast({ title: 'Portail créé', description: data.portal });
  };
  const handleUpdate = async (values) => {
    const { data } = await api.put(`/extranet/${editing.id}`, { ...values, users: Number(values.users), sessions: Number(values.sessions) });
    setClients((p) => p.map((x) => x.id === data.id ? data : x));
    toast({ title: 'Portail modifié' });
  };
  const handleDelete = async () => {
    await api.delete(`/extranet/${deleting.id}`);
    setClients((p) => p.filter((x) => x.id !== deleting.id));
    toast({ title: 'Portail supprimé' });
  };

  const openPortal = (c) => {
    const slug = (c.company || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    window.open(`/portal/${slug}`, '_blank', 'noopener');
  };
  const manageAccess = async (c) => {
    try {
      const { data } = await api.get(`/extranet/${c.id}/company-code`);
      try { await navigator.clipboard.writeText(data.access_code); } catch {}
      toast({
        title: 'Code entreprise copié',
        description: `${data.access_code} — collez-le dans l'onglet "Entreprise (RH)" du portail.`,
      });
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de récupérer le code.' });
    }
  };
  return (
    <div className="space-y-5">
      <div className="rounded-2xl p-6 flex items-center gap-6" style={{ background: 'linear-gradient(120deg, #E6FBF7 0%, #EEF2FF 100%)' }}>
        <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shrink-0">
          <Globe2 className="w-8 h-8 text-[#14B8A6]" />
        </div>
        <div className="flex-1">
          <h2 className="text-[22px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Portails extranet personnalisés</h2>
          <p className="text-[13.5px] text-[#4A4B63] mt-1">Un espace dédié pour chaque client entreprise, aux couleurs de votre organisation.</p>
        </div>
        <button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95">
          <Plus className="w-4 h-4" /> Créer un portail
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Portails actifs', value: 5, color: '#4F46E5' },
          { label: 'Utilisateurs connectés', value: 329, color: '#14B8A6' },
          { label: 'Sessions partagées', value: 79, color: '#F59E0B' },
          { label: 'Documents disponibles', value: 1842, color: '#3B82F6' },
        ].map((s, i) => (
          <div key={i} className="soft-card p-4">
            <div className="text-[12px] text-[#8E8FA5] font-medium">{s.label}</div>
            <div className="text-[22px] font-bold mt-0.5" style={{ color: s.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value.toLocaleString('fr-FR')}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {extranetClients.map((c) => (
          <div key={c.id} className="soft-card p-5 cursor-pointer">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#EEF2FF] to-[#E6FBF7] flex items-center justify-center shrink-0">
                  <span className="text-[14px] font-bold text-[#4F46E5]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.company[0]}</span>
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.company}</h3>
                  <div className="flex items-center gap-1 text-[11.5px] text-[#8E8FA5] mt-0.5">
                    <Link2 className="w-3 h-3" /> {c.portal}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(c)} className="w-8 h-8 rounded-lg hover:bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center" title="Modifier le portail">
                  <Settings2 className="w-4 h-4" />
                </button>
                <RowActions
                  onEdit={() => setEditing(c)}
                  onDelete={() => setDeleting(c)}
                />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#F3F4F8] grid grid-cols-3 gap-2">
              <div>
                <div className="flex items-center gap-1 text-[11px] text-[#8E8FA5]"><Users className="w-3 h-3" /> Utilisateurs</div>
                <div className="text-[15px] font-bold text-[#1A1B2E] mt-0.5">{c.users}</div>
              </div>
              <div>
                <div className="flex items-center gap-1 text-[11px] text-[#8E8FA5]">Sessions</div>
                <div className="text-[15px] font-bold text-[#1A1B2E] mt-0.5">{c.sessions}</div>
              </div>
              <div>
                <div className="flex items-center gap-1 text-[11px] text-[#8E8FA5]"><Eye className="w-3 h-3" /> Dern. conn.</div>
                <div className="text-[13px] font-semibold text-[#4A4B63] mt-0.5">{c.lastLogin}</div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => openPortal(c)} className="flex-1 h-9 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold hover:bg-[#EDE8FF] transition-colors flex items-center justify-center gap-1.5">
                <ExternalLink className="w-3.5 h-3.5" /> Ouvrir le portail
              </button>
              <button onClick={() => manageAccess(c)} className="flex-1 h-9 rounded-lg border border-[#EFF0F6] text-[#4A4B63] text-[12.5px] font-semibold hover:border-[#C7D2FE] transition-colors">Gérer l'accès</button>
            </div>
          </div>
        ))}
      </div>
      <FormDialog open={create} onOpenChange={setCreate} title="Nouveau portail extranet" description="Un portail dédié, aux couleurs de votre client entreprise." onSubmit={handleCreate} fields={FIELDS} />
      <FormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Modifier le portail" initialValues={editing} onSubmit={handleUpdate} fields={FIELDS} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Supprimer ce portail ?" description={deleting?.company} onConfirm={handleDelete} />
      <DetailSheet
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        title={detail?.company}
        subtitle={detail?.portal}
        rows={detail ? [
          { label: 'Utilisateurs', value: detail.users },
          { label: 'Sessions', value: detail.sessions },
          { label: 'Dernière connexion', value: detail.lastLogin },
          { label: 'URL du portail', value: detail.portal },
        ] : []}
      />
    </div>
  );
}
